import React from "react";
import { Box, Typography } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import HeartFilledSvg from "../../../../common/svgs/heartFilledSvg";
import CrossSvg from "../../../../common/svgs/crossSvg";
import CustomPopover from "../common/CustomPopover";
import { Theme } from "../../../../providers/ThemeProvider";

// preferred undefined → plain text row (no marker badge);
// optOut → checked-checkbox row ("Doesn't eat any …")
export type PopoverItem = { name: string; preferred?: boolean; optOut?: boolean };
export type PopoverGroup = { label: string; items: PopoverItem[] };

type Props = {
  open: boolean;
  items?: PopoverItem[];
  groups?: PopoverGroup[];
};

const CheckIcon = ({ fill }: { fill: string }) => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
    <path
      d="M10.725 0.725L3.75 7.7L1.275 5.225C0.975 4.925 0.525 4.925 0.225 5.225C-0.075 5.525 -0.075 5.975 0.225 6.275L3.225 9.275C3.375 9.425 3.525 9.5 3.75 9.5C3.975 9.5 4.125 9.425 4.275 9.275L11.775 1.775C12.075 1.475 12.075 1.025 11.775 0.725C11.475 0.425 11.025 0.425 10.725 0.725Z"
      fill={fill}
    />
  </svg>
);

const ItemRow = ({ item }: { item: PopoverItem }) => {
  const theme: Theme = useTheme();

  if (item.optOut) {
    return (
      <Box sx={styles.itemRow}>
        <Box sx={styles.optOutCheckbox(theme)}>
          <CheckIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />
        </Box>
        <Typography sx={styles.itemText(theme)}>{item.name}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.itemRow}>
      {item.preferred !== undefined && (
        <Box sx={styles.badge(theme, item.preferred)}>
          {item.preferred ? (
            <HeartFilledSvg
              fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE}
              width={12}
              height={12}
            />
          ) : (
            <CrossSvg
              fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE}
              width={12}
              height={12}
            />
          )}
        </Box>
      )}
      <Typography sx={styles.itemText(theme)}>{item.name}</Typography>
    </Box>
  );
};

const ExpandableListPopover = ({ open, items, groups }: Props) => {
  const theme: Theme = useTheme();

  return (
    <CustomPopover open={open}>
      <Box sx={styles.card(theme)}>
        {groups?.map((group, groupIndex) => (
          <Box key={group.label} sx={styles.group(theme, groupIndex > 0)}>
            <Typography sx={styles.groupLabel(theme)}>{group.label}</Typography>
            {group.items.map((item, index) => (
              <ItemRow key={`${item.name}-${index}`} item={item} />
            ))}
          </Box>
        ))}
        {items?.map((item, index) => (
          <ItemRow key={`${item.name}-${index}`} item={item} />
        ))}
      </Box>
    </CustomPopover>
  );
};

export default ExpandableListPopover;

const styles = {
  card: (theme: Theme) => ({
    my: 1,
    p: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    borderRadius: "8px",
    bgcolor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    boxShadow: "var(--box-shadow-general)",
    maxHeight: "300px",
    overflowX: "hidden",
    overflowY: "auto",
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    },
  }),
  group: (theme: Theme, hasDivider: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    ...(hasDivider && {
      borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
      paddingTop: "8px",
    }),
  }),
  groupLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "19px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    padding: "0 8px",
  }),
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "4px 8px",
  },
  badge: (theme: Theme, preferred: boolean) => ({
    width: "24px",
    height: "24px",
    minWidth: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: preferred
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colorTokens.D[70],
  }),
  optOutCheckbox: (theme: Theme) => ({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    margin: "0 2px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
  }),
  itemText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
};
